import { useEffect } from 'react';
import * as Sentry from '@sentry/react';

import { useInMemoryStorage } from '@ocx/data-in-memory-storage';

import { parseVroomPostMessage } from './vroom-post-message.utils';
import { LAST_VISITED_URL_KEY, LAST_VISITED_URL_TTL_MS } from './order-ahead-provider.vroom.constants';

export const useVroomStorage = (params: { iframeUrlTemplate: string }) => {
  const { iframeUrlTemplate } = params;
  const {
    value: lastVisitedUrl,
    setStorageValue: setLastVisitedUrl,
    eraseStorageValue: eraseLastVisitedUrl,
  } = useInMemoryStorage<string>(LAST_VISITED_URL_KEY);

  // Erase last visited URL if origin has changed
  useEffect(() => {
    try {
      if (!lastVisitedUrl) return;
      const { origin: iframeUrlTemplateOrigin } = new URL(iframeUrlTemplate);
      const { origin: lastVisitedUrlOrigin } = new URL(lastVisitedUrl);
      if (iframeUrlTemplateOrigin !== lastVisitedUrlOrigin) {
        eraseLastVisitedUrl();
      }
    } catch {
      Sentry.addBreadcrumb({ message: 'Error parsing URLs in useVroomStorage' });
      // 'new URL' can throw if lastVisitedUrl/iframeUrlTemplate is malformed
      // Preventively clearing the stored URL in such cases
      eraseLastVisitedUrl();
    }
  }, [iframeUrlTemplate, lastVisitedUrl, eraseLastVisitedUrl]);

  // Register the message listener
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      const { origin } = new URL(iframeUrlTemplate);
      const message = parseVroomPostMessage(event.data);

      if (event.origin !== origin) {
        return;
      }

      if (message.type === 'user_navigated') {
        const {
          data: { url },
        } = message;
        setLastVisitedUrl(url, LAST_VISITED_URL_TTL_MS);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeUrlTemplate, setLastVisitedUrl]);

  return { lastVisitedUrl };
};
